// DA-5090: a hiring Roleplay is drafted as a brief in four parts, stored as one
// text with each part under its heading (the BE's composeBrief):
//
//   Your role         who the candidate is, at what company
//   What you know     the facts, as bullets
//   What's happened   the events that create the problem, as bullets
//   Your task         the one thing to say
//
// The owner can edit it freely, so anything that doesn't follow that shape is
// shown as the plain text it is, line breaks kept.

import React from 'react';
import { Box, Typography } from '@mui/material';

const HEADINGS = ['Your role', 'What you know', "What's happened", 'Your task'];

export interface BriefPart {
  heading: string;
  lines: string[];
  bullets: boolean;
}

/** The parts, or null when the text isn't a brief (an older situation, or an owner's own wording). */
export function parseBrief(text: string): BriefPart[] | null {
  const blocks = String(text || '')
    .replace(/\r/g, '')
    .split(/\n\s*\n/)
    .map((b) => b.trim())
    .filter(Boolean);
  if (blocks.length < 2) return null;
  const parts = blocks.map((block) => {
    const [first, ...rest] = block.split('\n');
    return { heading: first.trim(), rest: rest.map((l) => l.trim()).filter(Boolean) };
  });
  if (!parts.every((p) => HEADINGS.includes(p.heading) && p.rest.length)) return null;
  return parts.map((p) => {
    const bullets = p.rest.every((l) => /^[•\-*]\s+/.test(l));
    return { heading: p.heading, bullets, lines: p.rest.map((l) => l.replace(/^[•\-*]\s+/, '')) };
  });
}

/** The brief as it reads aloud: no bullets, each part a short paragraph. */
export function spokenBrief(text: string): string {
  const parts = parseBrief(text);
  if (!parts) return text;
  return parts.map((p) => `${p.heading}. ${p.lines.map((l) => (/[.!?]$/.test(l) ? l : `${l}.`)).join(' ')}`).join('\n\n');
}

const RoleplayBrief: React.FC<{
  text: string;
  /** The text style of the surrounding page, so the brief sits in it. */
  textSx?: Record<string, unknown>;
  headingColor?: string;
}> = ({ text, textSx = {}, headingColor }) => {
  const parts = parseBrief(text);
  if (!parts) return <Typography sx={{ ...textSx, whiteSpace: 'pre-wrap' }}>{text}</Typography>;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {parts.map((part) => (
        <Box key={part.heading}>
          <Typography sx={{ ...textSx, fontWeight: 600, ...(headingColor ? { color: headingColor } : {}) }}>
            {part.heading}
          </Typography>
          {part.bullets ? (
            <Box component="ul" sx={{ m: 0, mt: '2px', pl: '20px' }}>
              {part.lines.map((line, i) => (
                <Typography component="li" key={i} sx={{ ...textSx }}>
                  {line}
                </Typography>
              ))}
            </Box>
          ) : (
            part.lines.map((line, i) => (
              <Typography key={i} sx={{ ...textSx }}>
                {line}
              </Typography>
            ))
          )}
        </Box>
      ))}
    </Box>
  );
};

export default RoleplayBrief;
